/// <amd-module name="VSSUI/Components/TreeEx/Tree" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone, FocusZoneContext, FocusZoneDirection, FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { getDefaultAnchorProps } from "VSSUI/Link";
import { IListMaterializedStats, IOverlayRenderProps, renderListCell } from "VSSUI/List";
import { UncheckedObserver } from "VSSUI/Observer";
import { ISimpleTableCell, ITable, ITableRow, renderColumns, renderLoadingCell, SimpleTableCell, Table } from "VSSUI/Table";
import { css, getSafeId, KeyCode, preventDefault } from "VSSUI/Util";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { ITree, ITreeColumn, ITreeProps, ITreeRowDetails, ITreeRowProps, TreeRowRenderer } from "./Tree.Props";
import { TreeExpand } from "./TreeExpand";

export class Tree<T> extends React.Component<ITreeProps<T>> implements ITree<T> {
    private table = React.createRef<Table<ITreeItemEx<T>>>();

    public render(): JSX.Element {
        const { role = this.props.role ? this.props.role : this.props.columns.length > 1 ? "treegrid" : "tree" } = this.props;
        // If we haven't specified an onActivate, but have specified an onToggle, toggle on activate.
        const onActivate = this.props.onActivate ? this.props.onActivate : this.props.onToggle ? this.onActivateExpand : undefined;

        return (
            <Table<ITreeItemEx<T>>
                ariaLabel={this.props.ariaLabel}
                behaviors={this.props.behaviors as Array<IBehavior<any, ITable<T>>>}
                className={this.props.className}
                columns={this.props.columns}
                containerClassName={this.props.containerClassName}
                eventDispatch={this.props.eventDispatch}
                focuszoneProps={this.props.focuszoneProps}
                id={this.props.id}
                itemProvider={this.props.itemProvider}
                maxHeight={this.props.maxHeight}
                onActivate={onActivate}
                onFocus={this.props.onFocus}
                onSelect={this.props.onSelect}
                pageSize={this.props.pageSize}
                renderHeader={this.props.renderHeader}
                renderRow={this.renderRow}
                renderSpacer={this.props.renderSpacer}
                role={role}
                rowHeight={this.props.rowHeight}
                ref={this.table}
                scrollable={this.props.scrollable}
                selectableText={this.props.selectableText}
                selection={this.props.selection}
                singleClickActivation={this.props.singleClickActivation}
                showHeader={this.props.showHeader}
                showLines={this.props.showLines}
                showScroll={this.props.showScroll}
                tableBreakpoints={this.props.tableBreakpoints}
                virtualize={this.props.virtualize}
            />
        );
    }

    public addOverlay(id: string, rowIndex: number, render: (props: IOverlayRenderProps) => React.ReactNode, zIndex: number = 0): void {
        if (this.table.current) {
            return this.table.current.addOverlay(id, rowIndex, render, zIndex);
        }
    }

    public removeOverlay(id: string): void {
        if (this.table.current) {
            return this.table.current.removeOverlay(id);
        }
    }

    public focusRow(rowIndex: number, direction: number = 1) {
        if (this.table.current) {
            return this.table.current.focusRow(rowIndex, direction);
        } else {
            return Promise.resolve();
        }
    }

    public getFocusIndex(): number {
        if (this.table.current) {
            return this.table.current.getFocusIndex();
        }

        return -1;
    }

    public getStats(): IListMaterializedStats {
        if (this.table.current) {
            return this.table.current.getStats();
        }

        return {
            firstMaterialized: -1,
            firstRendered: -1,
            lastMaterialized: -1,
            lastRendered: -1
        };
    }

    public scrollIntoView(rowIndex: number, options?: ScrollIntoViewOptions): void {
        if (this.table.current) {
            return this.table.current.scrollIntoView(rowIndex, options);
        }
    }

    private onActivateExpand = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<ITreeItemEx<T>>): void => {
        if (!event.defaultPrevented && tableRow.data.underlyingItem.childItems) {
            this.props.onToggle && this.props.onToggle(event, tableRow.data);
            event.preventDefault();
        }
    };

    private renderRow = (rowIndex: number, item: ITreeItemEx<T>, details: ITreeRowDetails<T>): JSX.Element => {
        // If onActivate for tree is not specified but onToggle is, tree passes on onActivateExpand as onActivate to table.
        // In that case, onActivate can be different for tree and table
        details.singleClickActivation = this.props.onActivate && details.singleClickActivation;

        if (this.props.columns.length <= 1 && !details.role) {
            details.role = "treeitem";
        }

        // Since the underlying table is unable to determine whether or not a row
        // is in a loading state since the observable value is within the ITreeItemEx
        // we need to handle this in the row rendering.
        return (
            <UncheckedObserver data={item.underlyingItem.data} key={item.underlyingItem.id}>
                {(props: { data?: T }) => {
                    if (props.data) {
                        // We need to forward the onToggle handler to the treeItemEx before it is rendered.
                        var canToggle = item.underlyingItem.childItems && item.underlyingItem.childItems.length !== 0; 
                        
                        // edge case: if row is a pipeline folder, we just know childItems exist, but not the length  
                        const anyData = props.data as any;
                        if (anyData.folder) {
                           canToggle = item.underlyingItem.childItems !== undefined; 
                        }

                        item.onToggle = canToggle ? this.props.onToggle : undefined;
                        
                        // First determine if the item supplied a custom row rendering function, if not
                        // attempt to use the global row rendering function.
                        const renderRow: TreeRowRenderer<T> = (item.underlyingItem.data as any).renderRow || this.props.renderRow;
                        if (renderRow) {
                            return renderRow(rowIndex, item, details);
                        }

                        return renderTreeRow(rowIndex, item, details, this.props.columns, props.data);
                    } else {
                        const { renderLoadingRow } = this.props;

                        // If a custom row loading animation is available use it.
                        if (renderLoadingRow) {
                            return renderLoadingRow(rowIndex, details);
                        }

                        // Return the default row loading animation.
                        return (
                            <TreeRow index={rowIndex} details={details}>
                                {this.props.columns.map((treeColumn: ITreeColumn<T>, columnIndex: number) => {
                                    let children = renderLoadingCell(treeColumn.columnLayout);

                                    if (treeColumn.hierarchical) {
                                        children = <TreeExpand depth={details.data.depth}>{children}</TreeExpand>;
                                    }

                                    return SimpleTableCell({ className: "bolt-tree-cell", columnIndex, children });
                                })}
                            </TreeRow>
                        );
                    }
                }}
            </UncheckedObserver>
        );
    };
}

export class TreeRow<T> extends React.Component<ITreeRowProps<T>> {
    private rowElement = React.createRef<HTMLTableRowElement | HTMLAnchorElement>();

    public render(): JSX.Element {
        const { details, index, linkProps } = this.props;
        const { ariaRowOffset, data, excludeFocusZone, renderSpacer, selectableText, selection, singleClickActivation } = details;
        // If the row is being rendered as a link we will use an anchor, otherwise we will
        // use a standard table row.
        const RowType = linkProps ? "a" : "tr";

        // Build the set of props needed from the link to forward on to the row element.
        const linkForwardProps = getDefaultAnchorProps(linkProps);

        return (
            <FocusWithin onFocus={this.onFocus}>
                {(focusStatus: IFocusWithinStatus) => {
                    return (
                        <FocusZoneContext.Consumer>
                            {rowContext => {
                                return (
                                    <FocusZone direction={FocusZoneDirection.Horizontal} postprocessKeyStroke={this.onPostprocessKeyStroke}>
                                        <RowType
                                            {...linkForwardProps}
                                            aria-busy={data === undefined}
                                            aria-expanded={
                                                // default to false if the item has children without an expanded value
                                                data && data.underlyingItem.childItems
                                                    ? data.underlyingItem.expanded === undefined
                                                        ? false
                                                        : data.underlyingItem.expanded
                                                    : undefined
                                            }
                                            aria-level={data ? data.depth + 1 : undefined}
                                            aria-rowindex={details?.role === "treeitem" ? undefined : index + ariaRowOffset}
                                            aria-selected={selection && selection.selected(index) ? true : undefined}
                                            className={css(
                                                this.props.className,
                                                "bolt-tree-row bolt-table-row bolt-list-row",
                                                index === 0 && "first-row",
                                                focusStatus.hasFocus && "focused",
                                                selection && selection.selected(index) && "selected",
                                                singleClickActivation && "single-click-activation",
                                                selectableText && "selectable-text",
                                                linkProps && "v-align-middle"
                                            )}
                                            data-focuszone={
                                                excludeFocusZone || (selection && !selection.selectable(index)) ? undefined : rowContext.focuszoneId
                                            }
                                            data-row-index={index}
                                            id={getSafeId(details.data.underlyingItem.id ?? index.toString())}
                                            onBlur={focusStatus.onBlur}
                                            onFocus={focusStatus.onFocus}
                                            onKeyDown={this.onKeyDown}
                                            ref={this.rowElement as any}
                                            role={details.role || "row"}
                                            tabIndex={getTabIndex(details)}
                                        >
                                            <td
                                                key="left-spacer"
                                                className="bolt-table-cell-compact bolt-table-cell bolt-list-cell"
                                                role="presentation"
                                            >
                                                {renderSpacer && renderSpacer(index, true)}
                                            </td>
                                            {this.props.children}
                                            <td
                                                key="right-spacer"
                                                className="bolt-table-cell-compact bolt-table-cell bolt-list-cell"
                                                role="presentation"
                                            >
                                                {renderSpacer && renderSpacer(index, false)}
                                            </td>
                                        </RowType>
                                    </FocusZone>
                                );
                            }}
                        </FocusZoneContext.Consumer>
                    );
                }}
            </FocusWithin>
        );
    }

    private onFocus = (event: React.FocusEvent<HTMLElement>) => {
        this.props.details.onFocusItem(this.props.index, event);
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (this.rowElement.current === event.nativeEvent.srcElement) {
                const { data } = this.props.details;
                if (data) {
                    if (data.onToggle) {
                        const { expanded } = data.underlyingItem;
                        if ((event.which === KeyCode.rightArrow && !expanded) || (event.which === KeyCode.leftArrow && expanded)) {
                            data.onToggle(event, data);
                            event.preventDefault();
                        }
                    }
                }
            }
        }
    };

    private onPostprocessKeyStroke = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.defaultPrevented) return FocusZoneKeyStroke.IgnoreNone;
        if (event.which !== KeyCode.leftArrow) return FocusZoneKeyStroke.IgnoreNone;

        const currentElement = this.rowElement.current;
        if (event.nativeEvent.srcElement !== currentElement) {
            currentElement?.focus();
            return FocusZoneKeyStroke.IgnoreNone;
        }

        const { data } = this.props.details;
        if (!data || !data.parentItem) return FocusZoneKeyStroke.IgnoreNone;

        let prevElement = currentElement?.previousElementSibling;
        let currentElementAriaLevel = currentElement?.getAttribute('aria-level');
        let prevElementAriaLevel = prevElement?.getAttribute('aria-level');
        while (prevElement && currentElement && currentElementAriaLevel && prevElementAriaLevel) {
            const currentLevel = Number.parseInt(currentElementAriaLevel);
            const prevElementLevel = Number.parseInt(prevElementAriaLevel);
            if (prevElementLevel < currentLevel) break;

            prevElement = prevElement.previousElementSibling;
        }

        if (data.parentItem.onToggle && prevElement?.id) {
            document.getElementById(prevElement.id)?.focus();
            data.parentItem.onToggle(event, data.parentItem);
        }

        event.preventDefault();
        return FocusZoneKeyStroke.IgnoreNone;
    };
}

export function renderTreeRow<T>(
    rowIndex: number,
    item: ITreeItemEx<T>,
    details: ITreeRowDetails<T>,
    columns: ITreeColumn<T>[],
    data?: T,
    className?: string,
    key?: React.Key | null
): JSX.Element {
    return (
        <TreeRow index={rowIndex} details={details} linkProps={data ? (data as any).linkProps : undefined} className={className} key={key}>
            {renderColumns(rowIndex, columns, item, details)}
        </TreeRow>
    );
}

/**
 * Standard cell renderer for a tree cell with expandable children. This will use the tree items
 * state to determine whether or not the row is expanded etc.
 */
export function ExpandableTreeCell<T>(props: {
    children?: React.ReactNode;
    className?: string;
    colspan?: number;
    columnIndex: number;
    contentClassName?: string;
    treeColumn?: ITreeColumn<T>;
    treeItem: ITreeItemEx<T>;
    role?: string;
}) {
    const { colspan, columnIndex, contentClassName, treeItem, treeColumn, role } = props;
    const { depth, onToggle, underlyingItem } = treeItem;
    const { expanded } = underlyingItem;

    const children = (
        <TreeExpand
            expanded={expanded}
            depth={depth}
            indentationSize={treeColumn && treeColumn.indentationSize}
            onClick={preventDefault}
            onToggle={onToggle ? event => onToggle(event, treeItem) : undefined}
        >
            {props.children}
        </TreeExpand>
    );

    return SimpleTableCell({
        children,
        className: css(props.className, "bolt-tree-cell"),
        colspan,
        columnIndex,
        contentClassName,
        tableColumn: treeColumn,
        role: role
    });
}

export function renderExpandableTreeCell<T extends ISimpleTableCell>(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<T>,
    treeItem: ITreeItemEx<T>,
    ariaRowIndex?: number | undefined,
    role?: string
): JSX.Element {
    const { underlyingItem } = treeItem;
    const data = ObservableLike.getValue(underlyingItem.data);
    const treeCell = data && data[treeColumn.id];

    // Do not include padding if the table cell has an href
    const hasLink = !!(treeCell && typeof treeCell !== "string" && typeof treeCell !== "number" && treeCell.href);

    return ExpandableTreeCell({
        children: treeCell && renderListCell(treeCell),
        className: treeColumn.className,
        columnIndex,
        contentClassName: hasLink ? "bolt-table-cell-content-with-link" : undefined,
        treeItem,
        treeColumn,
        role
    });
}

export function renderTreeCell<T extends ISimpleTableCell>(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<T>,
    treeItem: ITreeItemEx<T>,
    ariaRowIndex?: number | undefined,
    role?: string
): JSX.Element {
    const { underlyingItem } = treeItem;
    const data = ObservableLike.getValue(underlyingItem.data);
    const treeCell = data && data[treeColumn.id];

    // Do not include padding if the table cell has an href
    const hasLink = !!(treeCell && typeof treeCell !== "string" && typeof treeCell !== "number" && treeCell.href);

    return SimpleTableCell({
        className: treeColumn.className,
        children: treeCell && renderListCell(treeCell),
        columnIndex,
        contentClassName: hasLink ? "bolt-table-cell-content-with-link" : undefined,
        tableColumn: treeColumn,
        role: role
    });
}

export function renderTreeCellWithClassName<T extends ISimpleTableCell>(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<T>,
    treeItem: ITreeItemEx<T>,
    contentClassName: string
): JSX.Element {
    const { underlyingItem } = treeItem;
    const data = ObservableLike.getValue(underlyingItem.data);
    const treeCell = data && data[treeColumn.id];

    // Do not include padding if the table cell has an href
    const hasLink = !!(treeCell && typeof treeCell !== "string" && typeof treeCell !== "number" && treeCell.href);

    return SimpleTableCell({
        className: treeColumn.className,
        children: treeCell && renderListCell(treeCell),
        columnIndex,
        contentClassName: css(contentClassName, hasLink ? "bolt-table-cell-content-with-link" : undefined),
        tableColumn: treeColumn
    });
}
